import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  IInvestmentReview,
  IRiskSignature,
  ITermSignature,
  IInvestmentConclusion,
  IProductInfoResponse,
  getProductSelection,
  getProductInfo,
  getProductInvestmentReviewByCategory,
  makeProductInvestmentByCategory,
  getProductPendingRiskSignature,
  IStructuredOperationsSelection,
  getPendingTermSignatureById
} from '@/modules/transaction/services/product-investment';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { ProductInvestmentComponents } from './helpers';
import { getPartner, isImpersonate } from '@/modules/common/services/partner';
import { EProductInvestmentSteps } from './helpers/step-components';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductInvestment',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const currentStep = ref(EProductInvestmentSteps.INVESTMENT);
const investmentReview = ref<IInvestmentReview>();
const investmentConclusion = ref<IInvestmentConclusion>();
const structuredOperationsSelection = ref<IStructuredOperationsSelection>();
const productInfo = ref<IProductInfoResponse>();

const isLoading = ref(true);
const isFetchingProduct = ref(false);
const isFetchingReview = ref(false);
const isFetchingInvestment = ref(false);
const hasError = ref(false);
const amount = ref(0);
const productValue = ref(0);

const riskSignature = ref<IRiskSignature>();
const termSignature = ref<ITermSignature>();

const useBaseAssetChoice = ref<boolean>(false);

const step = computed(() => {
  return {
    component: ProductInvestmentComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return currentStep.value !== EProductInvestmentSteps.CONCLUSION;
});

const productId = computed(() => {
  const { id } = route.params;
  return id;
});

const productType = computed(() => {
  const { type } = route.params;
  return type;
});

function onBack() {
  if (
    currentStep.value === EProductInvestmentSteps.INVESTMENT ||
    currentStep.value === EProductInvestmentSteps.SELECTION
  ) {
    return onClose();
  }

  currentStep.value = currentStep.value - 1;
}

function onClose() {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    router.push({
      name: 'productsCatalog',
      params: {
        type: productType.value
      }
    });
  }
}

async function getDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    await Promise.all([
      route.query.selection
        ? fetchProductSelection()
        : fetchProductInfo(productId.value),
      fetchProductRiskSignature(),
      fetchProductTermSignature()
    ]);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchProductSelection() {
  structuredOperationsSelection.value = await getProductSelection(
    productId.value,
    productType.value
  );
}

async function fetchProductInfo(
  productIdParam: string,
  quantity?: number,
  useBaseAssetParam?: boolean
) {
  try {
    isFetchingProduct.value = true;

    productInfo.value = await getProductInfo(
      productIdParam,
      productType.value,
      quantity,
      useBaseAssetParam
    );

    currentStep.value = EProductInvestmentSteps.INVESTMENT;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });

    router.back();
  } finally {
    isFetchingProduct.value = false;
  }
}

async function fetchProductRiskSignature() {
  riskSignature.value = await getProductPendingRiskSignature(
    productId.value,
    productType.value
  );
}

async function fetchProductTermSignature() {
  termSignature.value = await getPendingTermSignatureById(
    productId.value,
    productType.value
  );
}

async function fetchProductInvestmentReview(
  amountValue: number,
  useBaseAsset?: boolean
) {
  amount.value = amountValue;

  try {
    isFetchingReview.value = true;

    investmentReview.value = await getProductInvestmentReviewByCategory(
      productId.value,
      amount.value,
      productType.value,
      useBaseAsset
    );

    if (investmentReview.value) {
      productValue.value = investmentReview.value.amount ?? 0;
    }

    currentStep.value = EProductInvestmentSteps.REVIEW;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingReview.value = false;
  }
}

async function makeInvestment(useBaseAsset?: boolean) {
  try {
    isFetchingInvestment.value = true;

    investmentConclusion.value = await makeProductInvestmentByCategory(
      productId.value,
      investmentReview.value?.reservationAmount,
      amount.value,
      productType.value,
      isImpersonate() ? getPartner()?._id : undefined,
      useBaseAsset
    );

    currentStep.value = EProductInvestmentSteps.CONCLUSION;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingInvestment.value = false;
  }
}

onMounted(getDetails);
onBeforeMount(() => {
  route.query.selection
    ? (currentStep.value = EProductInvestmentSteps.SELECTION)
    : (currentStep.value = EProductInvestmentSteps.INVESTMENT);

  if (!route.query.selection) {
    useBaseAssetChoice.value = false;
  }
});

provide('currentStep', currentStep);
provide('productId', productId);
provide('productType', productType);
provide('productInfo', productInfo);
provide('investmentReview', investmentReview);
provide('investmentConclusion', investmentConclusion);
provide('structuredOperationsSelection', structuredOperationsSelection);
provide('riskSignatureData', riskSignature);
provide('isFetchingReview', isFetchingReview);
provide('termSignatureData', termSignature);
provide('isFetchingInvestment', isFetchingInvestment);
provide('isFetchingProduct', isFetchingProduct);
provide('useBaseAssetChoice', useBaseAssetChoice);

provide('fetchInvestmentReview', fetchProductInvestmentReview);
provide('makeInvestment', makeInvestment);
provide('fetchProductInfo', fetchProductInfo);

return { __sfc: true,route, router, currentStep, investmentReview, investmentConclusion, structuredOperationsSelection, productInfo, isLoading, isFetchingProduct, isFetchingReview, isFetchingInvestment, hasError, amount, productValue, riskSignature, termSignature, useBaseAssetChoice, step, showNavbar, productId, productType, onBack, onClose, getDetails, fetchProductSelection, fetchProductInfo, fetchProductRiskSignature, fetchProductTermSignature, fetchProductInvestmentReview, makeInvestment, NavBar, LoaderIndicator, HelperViewStatus, BackButton, FadeTransition }
}

})